/**
 * 소리의 종류. 화면과 전장은 "무슨 일이 났다"(`CueId`)만 말하고, 어떤 소리로 낼지는 뒤쪽(`synth.ts`)이 정한다 —
 * 소리를 샘플로 바꾸거나 다시 고를 때 부르는 쪽을 건드리지 않는다.
 *
 * 이 묶음에는 **판의 상태만으로 틀림없이 알 수 있는 일**만 있다: 장수 기술, 동료 기술, 대장이 쓰러짐, 판의 끝, 단추.
 * 타격·화살·말발굽은 없다. 피해 숫자가 쓰는 길(`render/damage-floats.ts`)은 프레임 사이의 체력 차이라 누가 무엇으로
 * 쳤는지 모르고, 숫자 예산을 넘은 것은 다음 프레임으로 미룬다 — 거기에 소리를 달면 여러 타격이 하나로 뭉치고 늦게
 * 난다(계획 리뷰). 그 소리들은 표현 전용 사건 기록을 따로 둔 뒤에 붙인다.
 */
export type CueId =
  | 'ui.tap'
  | 'ui.on'
  | 'skill.roar'
  | 'skill.phalanx'
  | 'skill.volley'
  | 'mate.skill'
  | 'chief.fall'
  | 'sub.fall'
  | 'hero.fall'
  | 'win'
  | 'lose';

export interface CueDef {
  /** 단추 소리는 전장 소리와 자리를 다투지 않는다(동시 발음 수를 따로 센다). */
  readonly bus: 'ui' | 'battle';
  /** 클수록 먼저. 자리가 찼을 때 낮은 것은 버린다. `KEEP` 이상은 자리가 차도 낸다. */
  readonly pri: number;
  /** 같은 소리를 다시 내기까지의 틈(ms). 한 틱에 여럿이 걸려도 한 번만 난다. */
  readonly gapMs: number;
  /** 소리가 들리는 길이(ms) — 동시 발음 수를 세는 데 쓴다. 꼭대기의 1/1000 아래로 잦아든 때(`npm run sound-peaks`의
   *  "잦아듦")보다 조금 길게 둔다. 마디가 멈추는 때는 이보다 늦다(들리지 않는 꼬리) — 소리를 자르는 값이 아니다. */
  readonly durMs: number;
  /** 사용자에게 보이는 이름(들어 보기 목록). */
  readonly label: string;
}

/** 이 순위부터는 자리가 차도 낸다 — 판의 끝이나 장수가 쓰러진 것을 타격 소리에 묻지 않는다. */
export const KEEP = 3;

export const CUES: Readonly<Record<CueId, CueDef>> = {
  'ui.tap': { bus: 'ui', pri: 0, gapMs: 45, durMs: 60, label: '단추' },
  'ui.on': { bus: 'ui', pri: 1, gapMs: 200, durMs: 260, label: '소리 켬' },
  'skill.roar': { bus: 'battle', pri: 3, gapMs: 300, durMs: 600, label: '함성' },
  'skill.phalanx': { bus: 'battle', pri: 3, gapMs: 300, durMs: 850, label: '방진' },
  'skill.volley': { bus: 'battle', pri: 3, gapMs: 300, durMs: 750, label: '일제사격' },
  'mate.skill': { bus: 'battle', pri: 2, gapMs: 250, durMs: 500, label: '동료 기술' },
  'chief.fall': { bus: 'battle', pri: 4, gapMs: 400, durMs: 2600, label: '적장이 쓰러짐' },
  'sub.fall': { bus: 'battle', pri: 2, gapMs: 400, durMs: 550, label: '적 부장이 쓰러짐' },
  'hero.fall': { bus: 'battle', pri: 4, gapMs: 1000, durMs: 2200, label: '장수가 쓰러짐' },
  win: { bus: 'battle', pri: 5, gapMs: 1000, durMs: 2000, label: '이김' },
  lose: { bus: 'battle', pri: 5, gapMs: 1000, durMs: 2350, label: '짐' },
};

export const CUE_IDS = Object.keys(CUES) as CueId[];
